import objectHash from "object-hash";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { debounceTime, Subject } from "rxjs";
import { getApiError } from "@entrilia/shared/api/axiosHelper.ts";
import type { ApiResponse } from "@entrilia/shared/api/types.ts";
import { logError } from "@entrilia/shared/logging.ts";
import type {
  BuildFinancialStatementReportRequest,
  FailedMeasure,
  FinancialStatementResponse,
} from "../../../../../../../../api/types/svTypes.ts";
import { useEnhancedBiApiClientProvider } from "../../../../contexts/ApiClientProviderContext.ts";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch.ts";
import {
  selectCacheSessionId,
  selectCurrentReportId,
  selectRefreshToken,
  selectSessionId,
} from "../../../../store/selectors/currentReportSelectors.ts";
import { selectLogQueries } from "../../../../store/selectors/devToolsSelectors.ts";
import {
  selectConditions,
  selectFinancialStatementConfiguration,
} from "../../../../store/selectors/fieldsStateSelectors.ts";
import { selectDimensions } from "../../../../store/selectors/metaDataSelectors.ts";
import { createCurrentReportAction, createDevToolsAction } from "../../../../store/utils/storeUtils.ts";
import {
  isFinancialStatementConfigurationValid,
  type ConfigurationValidReturnType,
} from "../../../../utilities/isConfigurationValid.ts";
import useDeferredBuild from "../../common/hooks/useDeferredBuild.ts";
import { getBuildInputs } from "../utils/statementBuildInputs.ts";

export interface FinancialStatementBuildState {
  statement: FinancialStatementResponse | undefined;
  calculating: boolean;
  error: string | undefined;
  failedMeasures: FailedMeasure[];
  configurationValidity: ConfigurationValidReturnType;
}

const CANCELED_REASON = "canceled";
const BUILD_ERROR_CONTEXT = "[FinancialStatementPreview] buildFinancialStatementReport";
const DEBOUNCE_MS = 100;
const missingConfigurationValidity: ConfigurationValidReturnType = { valid: false, validation: "error" };

const useDataLoadingBuilder = (): FinancialStatementBuildState => {
  const dispatch = useAppDispatch();
  const conditions = useSelector(selectConditions);
  const configuration = useSelector(selectFinancialStatementConfiguration);
  const dimensions = useSelector(selectDimensions);
  const reportId = useSelector(selectCurrentReportId);
  const sessionId = useSelector(selectSessionId);
  const cacheSessionId = useSelector(selectCacheSessionId);
  const isQueryLoggingActive = useSelector(selectLogQueries);
  const refreshToken = useSelector(selectRefreshToken);
  const { buildFinancialStatementReport } = useEnhancedBiApiClientProvider();
  const [calculateCount, setCalculateCount] = useState(0);
  const [statement, setStatement] = useState<FinancialStatementResponse>();
  const [error, setError] = useState<string>();
  const [failedMeasures, setFailedMeasures] = useState<FailedMeasure[]>([]);
  const abortControllerRef = useRef<AbortController | undefined>(undefined);
  const subjectDelay = useMemo(() => new Subject<string>(), []);

  const request = useMemo(
    (): BuildFinancialStatementReportRequest | undefined =>
      configuration
        ? {
            ...configuration,
            conditions: conditions.map((condition) => ({
              ...condition.config,
              invalid: condition.validation?.invalid,
            })),
            reportId: reportId ?? "",
            sessionId,
            cacheSessionId: cacheSessionId ?? "",
            withDrillDown: false,
            useQueryLogging: isQueryLoggingActive === true,
          }
        : undefined,
    [cacheSessionId, conditions, configuration, isQueryLoggingActive, reportId, sessionId]
  );

  const buildInputsHash = useMemo(
    () => objectHash(getBuildInputs(configuration, request?.conditions ?? [])),
    [configuration, request]
  );

  const configurationValidity = useMemo(
    () =>
      configuration
        ? isFinancialStatementConfigurationValid(conditions, configuration, dimensions)
        : missingConfigurationValidity,
    [conditions, configuration, dimensions]
  );

  const handleBuildDone = (response: ApiResponse<FinancialStatementResponse>) => {
    if (response.success) {
      if (response.data.errorCode) {
        setError(response.data.errorCode);
      } else {
        setError(undefined);
        setStatement(response.data);
        dispatch(createDevToolsAction("update", { loggingItems: response.data.loggingItems }));
        dispatch(createCurrentReportAction("setEtag", { etag: response.data.etag, cacheValid: undefined }));
        setFailedMeasures(response.data.failedMeasures);
      }
    } else if (response.error?.message) {
      logError(response.error, BUILD_ERROR_CONTEXT);
      setError(response.error.message);
    }
    setCalculateCount((count) => count - 1);
  };

  const handleBuildError = (reason: string | undefined) => {
    if (reason === CANCELED_REASON) {
      setError(undefined);
    } else {
      setError(reason || "An error occurred while calculating report data. Please try again.");
    }
    setCalculateCount((count) => count - 1);
  };

  const calculate = (
    onBuildDone: (response: ApiResponse<FinancialStatementResponse>) => void,
    onBuildError: (reason: string | undefined) => void
  ) => {
    if (!buildFinancialStatementReport || !request) {
      return;
    }
    setCalculateCount((count) => count + 1);
    setError(undefined);
    dispatch(createCurrentReportAction("setEtag", { etag: undefined, cacheValid: undefined }));
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    void buildFinancialStatementReport(request, abortController.signal).then(onBuildDone, (reason: unknown) => {
      const apiError = getApiError(reason);
      if (apiError.isCanceledRequest) {
        onBuildError(CANCELED_REASON);
        return;
      }
      logError(apiError, BUILD_ERROR_CONTEXT);
      onBuildError(apiError.message);
    });
  };

  const { doRequest, resetInitialRequestFlag, cancelCurrentAwaiter } = useDeferredBuild({
    request: calculate,
    handleBuildDone,
    handleBuildError,
    cancelRequest: () => abortControllerRef.current?.abort(),
    onProgressChange: (progress) => dispatch(createCurrentReportAction("updateBuildProgress", progress)),
  });

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    resetInitialRequestFlag();
  }, [reportId, resetInitialRequestFlag]);

  useLayoutEffect(() => {
    const subscription = subjectDelay.pipe(debounceTime(DEBOUNCE_MS)).subscribe(doRequest);
    return () => subscription.unsubscribe();
  }, [doRequest, subjectDelay]);

  useEffect(() => {
    if (configurationValidity.valid) {
      subjectDelay.next(refreshToken);
    } else {
      cancelCurrentAwaiter();
    }
  }, [buildInputsHash, cancelCurrentAwaiter, configurationValidity.valid, refreshToken, subjectDelay]);

  return { statement, calculating: calculateCount > 0, error, failedMeasures, configurationValidity };
};

export default useDataLoadingBuilder;
